import { FunctionComponent, useContext, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import Table, { Column, Pagination } from "../components/table/Table";
import { ConversionHistory, User } from "../utils/helpers/types";
import Input from "../components/input/Input";
import { resources } from "../utils/resources";
import {
  getRequestHistory,
  reDownloadFile,
} from "../utils/helpers/data/upload-convert";
import UserContext from "../context/user-context";
import LayoutContext from "../context/layout-context";
import { initialPaginationData } from "../utils/constants";
import Button from "../components/button/Button";

const tagColors = ["#e0eaf1", "#f5e961", "#c7e5b2", "#61e8f5"];
const tagColorMapping: Record<string, string> = {};

const getRandomTagColor: (tag: string) => string = (tag) => {
  if (tagColorMapping[tag]) {
    return tagColorMapping[tag];
  }
  if (!tagColors.length) {
    return "#e0eaf1";
  }
  const [color] = tagColors.splice(
    Math.floor(Math.random() * tagColors.length),
    1
  );
  tagColorMapping[tag] = color;
  return tagColorMapping[tag];
};

let timerRef: any;

const IndexPage: FunctionComponent = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<ConversionHistory[]>([]);
  const [searchStr, setSearchStr] = useState("");
  const [pagination, setPagination] = useState<Pagination>({
    ...initialPaginationData,
  });
  const [downloadInProgressMap, setDownloadInProgressMap] = useState<
    Record<number, boolean>
  >({});
  const [domainFilterStr, setDomainFilterStr] = useState("");

  const user = useContext(UserContext);
  const { notify } = useContext(LayoutContext);

  const fetchHistory = async (pageNumber = pagination.pageNumber) => {
    setLoading(true);
    const { data, message, error } = await getRequestHistory({
      pageNumber,
      pageSize: pagination.pageSize,
      user: user as User,
      searchStr,
      institutionFilter: domainFilterStr,
    });
    setLoading(false);
    if (error) {
      notify("error", `Unable to fetch history: ${message}`);
      return;
    }
    setDataSource(data?.history || []);
    setPagination({ ...pagination, total: data?.count || 0, pageNumber });
  };

  useEffect(() => {
    localStorage.setItem("MC_ABU_SAVED_PAGE_SIZE", String(pagination.pageSize));
    fetchHistory();
  }, [pagination.pageNumber, pagination.pageSize]);

  useEffect(() => {
    clearTimeout(timerRef);
    timerRef = setTimeout(() => fetchHistory(1), 300);
  }, [searchStr, domainFilterStr]);

  const columns: Column[] = [
    {
      title: "Request ID",
      key: "key",
      dataIndex: "key",
    },
    {
      title: "Requester Username",
      key: "userName",
      dataIndex: "userName",
    },
    {
      title: "Organization",
      key: "organization",
      dataIndex: "organization",
      render: (org) => (
        <strong
          role="button"
          className="tag clickable neutral"
          onClick={() => setDomainFilterStr(org)}
          title="Click to filter"
          style={{ backgroundColor: getRandomTagColor(org) }}
        >
          {org}
        </strong>
      ),
    },
    {
      title: "Date",
      key: "date",
      dataIndex: "date",
      render: (date: Dayjs) => date.format(" D MMM YYYY"),
    },
    {
      title: "Response File",
      key: "file",
      dataIndex: "file",
      render: (_, record) => (
        <Button
          size="small"
          loading={downloadInProgressMap[record.key]}
          onClick={() => handleDownload(record)}
        >
          Download
        </Button>
      ),
    },
  ];

  const handleDownload = async (record: ConversionHistory) => {
    setDownloadInProgressMap({ ...downloadInProgressMap, [record.key]: true });
    const response = await reDownloadFile(record.transId, user as User);
    setDownloadInProgressMap({ ...downloadInProgressMap, [record.key]: false });
    if (response.error) {
      notify("error", `Unable to re-download file: ${response.message}`);
      return;
    }
    const elem = document.createElement("a");
    elem.setAttribute(
      "href",
      `data:text/plain;charset=utf-8,${encodeURIComponent(response.data || "")}`
    );
    elem.setAttribute("download", `abu-mandate-output-${record.key}`);
    elem.click();
  };

  return (
    <main className="content-layout">
      <h1 className="title vertical-margin spaced thin">Conversion History</h1>
      <div
        className={[
          "search-wrapper",
          user?.domainCode === "ISW" && "flex between center-align",
        ].join(" ")}
      >
        <Input
          icon={resources.icons.SEARCH}
          placeholder="Search by username"
          value={searchStr}
          onChange={setSearchStr}
          className={user?.domainCode === "ISW" ? "half-width" : undefined}
          onInputClear={() => setSearchStr("")}
        />
        {user?.domainCode === "ISW" && (
          <div className="flex column">
            <strong className="smaller">Filter by organization code</strong>
            <Input
              placeholder="Enter code"
              value={domainFilterStr}
              onChange={setDomainFilterStr}
              onInputClear={() => setDomainFilterStr("")}
            />
          </div>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={pagination}
        onPageChange={(pageNumber) =>
          setPagination({ ...pagination, pageNumber })
        }
        onPageSizeChange={(pageSize) =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
      />
    </main>
  );
};

export default IndexPage;
